import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  signIn as apiSignIn,
  completeNewPassword as apiCompleteNewPassword,
  getCurrentSession,
  refreshSession,
  signOut as apiSignOut,
} from './session';
import type { PendingNewPassword } from './session';
import { AuthContext, type AuthCtx } from './context';
import type { AuthUser, PendingChallenge } from './types';

interface Props {
  children: ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingChallenge, setPendingChallenge] = useState<PendingChallenge | null>(null);
  const [pendingCognito, setPendingCognito] = useState<PendingNewPassword | null>(null);

  // Rehidratar sesión al montar: la cookie httpOnly se envía sola.
  // /auth/session siempre responde 200 con { isAuthenticated, hasRefreshToken,
  // user }. Si ya hay sesión válida, se usa directo. Si el backend confirmó
  // que no hay refresh_token (hasRefreshToken:false con isLegacyOrUnknown
  // ausente), el usuario queda anónimo sin disparar ninguna llamada extra.
  // Si en cambio no pudimos confirmar el estado (isLegacyOrUnknown: backend
  // viejo, red caída, 5xx) igual se intenta /auth/refresh — más vale una
  // llamada de más que deslogear a alguien con sesión real durante un
  // deploy desincronizado.
  useEffect(() => {
    getCurrentSession()
      .then(async session => {
        if (session.isAuthenticated && session.user) {
          setUser(session.user);
          return;
        }
        if (!session.hasRefreshToken && !session.isLegacyOrUnknown) return;
        try {
          setUser(await refreshSession());
        } catch {
          // No hay sesión recuperable — el usuario queda deslogueado.
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const outcome = await apiSignIn(email, password);
    if (outcome.kind === 'session') {
      setUser(outcome.user);
      setPendingChallenge(null);
      setPendingCognito(null);
    } else {
      setPendingCognito(outcome);
      setPendingChallenge({ kind: 'new-password-required', email });
    }
  }, []);

  const completeNewPassword = useCallback(async (newPassword: string) => {
    if (!pendingCognito) throw new Error('No hay challenge pendiente');
    const sessionUser = await apiCompleteNewPassword(pendingCognito, newPassword);
    setUser(sessionUser);
    setPendingChallenge(null);
    setPendingCognito(null);
  }, [pendingCognito]);

  const signOut = useCallback(async () => {
    await apiSignOut();
    setUser(null);
    setPendingChallenge(null);
    setPendingCognito(null);
  }, []);

  const value = useMemo<AuthCtx>(() => ({
    user,
    loading,
    pendingChallenge,
    signIn,
    completeNewPassword,
    signOut,
  }), [user, loading, pendingChallenge, signIn, completeNewPassword, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
